import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { getServiceById, getCategoryById, getRelatedServices, formatViews, formatDate } from '@/lib/content';
import ServiceCard from '@/components/base/ServiceCard';
import EmptyState from '@/components/base/EmptyState';
import CategorySidebar from '@/components/feature/CategorySidebar';

export default function Detail() {
  const { id } = useParams();
  const service = getServiceById(id);
  const category = getCategoryById(service?.categoryId);
  const [toast, setToast] = useState('');

  useSeo({
    title: service ? `${service.title} - 在线详细介绍` : '内容详情 - 在线服务平台',
    description: service ? service.summary : '查看在线服务详细信息。',
    keywords: service ? [service.title, ...service.tags].join(',') : '在线服务详情',
  });

  if (!service) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          icon="ri-error-warning-line"
          title="内容不存在或已下线"
          description="这条服务信息可能已被移除或链接有误，返回首页看看其他内容吧。"
        />
      </div>
    );
  }

  const related = getRelatedServices(service, 3);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2200);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast('链接已复制到剪贴板');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
        <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/categories" className="hover:text-primary-600 transition-colors">全部分类</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${service.categoryId}`} className="hover:text-primary-600 transition-colors">{category?.name}</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800 truncate max-w-[12rem]">{service.title}</span>
      </nav>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        <div className="w-full lg:w-60 shrink-0 order-2 lg:order-1">
          <div className="lg:sticky lg:top-24">
            <CategorySidebar activeId={service.categoryId} />
          </div>
        </div>

        <article className="flex-1 min-w-0 order-1 lg:order-2">
          <div className="relative w-full h-[240px] md:h-[400px] rounded-lg overflow-hidden bg-background-100">
            <img
              src={service.image}
              alt={service.title}
              title={`${service.title} - 在线详细介绍`}
              className="w-full h-full object-cover object-top"
            />
            <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-background-50/90 text-primary-700 text-xs md:text-sm font-medium">
              {category?.name} · {service.subCategory}
            </span>
          </div>

          <h1 className="mt-6 text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">{service.title}</h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5"><i className="ri-store-2-line"></i>{service.provider}</span>
            <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>{formatDate(service.publishedAt)}</span>
            <span className="flex items-center gap-1.5"><i className="ri-map-pin-line"></i>{service.region}</span>
            <span className="flex items-center gap-1.5"><i className="ri-eye-line"></i>{formatViews(service.views)} 次浏览</span>
            <span className="flex items-center gap-1.5 text-accent-600"><i className="ri-star-fill"></i>{service.rating} 分</span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {service.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-800">#{tag}</span>
            ))}
          </div>

          <div className="mt-8 rounded-lg bg-primary-50 border border-primary-100 p-4 md:p-5">
            <h2 className="text-sm font-semibold text-primary-700 mb-2 flex items-center gap-2">
              <i className="ri-file-text-line"></i>内容简介
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{service.summary}</p>
          </div>

          <section className="mt-8">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-3">详细介绍</h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed whitespace-pre-line">{service.description}</p>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              温馨提示：本页内容为平台演示数据，具体服务详情、价格与规则请以实际提供方公布的信息为准。如需进一步了解，可通过下方联系方式咨询。
            </p>
          </section>

          <section className="mt-8 rounded-lg bg-background-100 border border-background-200 p-4 md:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-foreground-900 flex items-center gap-2">
                  <i className="ri-customer-service-2-line text-primary-600"></i>联系方式
                </h2>
                <p className="text-sm text-foreground-500 mt-1.5">提供方：{service.provider} · 服务地区：{service.region}</p>
                <p className="text-sm text-foreground-500 mt-1 flex items-center gap-1.5">
                  <span className="px-2 py-0.5 text-xs rounded-md bg-accent-100 text-accent-700">前端演示</span>
                  以下咨询为演示按钮，不产生真实提交
                </p>
              </div>
              <button
                type="button"
                onClick={() => showToast('演示按钮：正式上线可对接真实咨询渠道')}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-chat-1-line"></i>
                在线咨询
              </button>
            </div>
          </section>

          <section className="mt-6">
            <h2 className="text-base font-semibold text-foreground-900 mb-3 flex items-center gap-2">
              <i className="ri-share-forward-line text-primary-600"></i>分享这条内容
            </h2>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={copyLink} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-link"></i>复制链接
              </button>
              <button type="button" onClick={() => showToast('已生成分享文案，可粘贴到社交平台')} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-wechat-line"></i>微信
              </button>
              <button type="button" onClick={() => showToast('已生成分享文案，可粘贴到社交平台')} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-weibo-line"></i>微博
              </button>
            </div>
          </section>

          {related.length > 0 && (
            <section className="mt-10 pt-8 border-t border-background-200">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-5">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {related.map((item) => (
                  <ServiceCard key={item.id} service={item} />
                ))}
              </div>
            </section>
          )}
        </article>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}